function compact(objectOrArray, checkDefineFn = (value) => value !== void 0) {
  if (isArray(objectOrArray)) {
    return objectOrArray.map((value) => clearUndefined(value, checkDefineFn)).filter((element) => checkDefineFn(element));
  }
  if (isObject(objectOrArray)) {
    return Object.fromEntries(
      Object.entries(objectOrArray).map((value) => clearUndefined(value, checkDefineFn)).filter(([_, value]) => clearUndefined(value, checkDefineFn))
    );
  }
  return objectOrArray;
}
function clearUndefined(objectOrArray, checkDefineFn = (value) => value !== void 0) {
  if (isArray(objectOrArray)) {
    return objectOrArray.map((value) => clearUndefined(value, checkDefineFn)).filter((element) => checkDefineFn(element));
  }
  if (isObject(objectOrArray)) {
    return Object.fromEntries(
      Object.entries(objectOrArray).map((value) => clearUndefined(value, checkDefineFn)).filter(([_, value]) => clearUndefined(value, checkDefineFn))
    );
  }
  return objectOrArray;
}
const cloneDeep = (value) => {
  return JSON.parse(JSON.stringify(value));
};
const sleep = (timeout) => {
  return new Promise((resolve) => setTimeout(resolve, timeout));
};
const toFunction = (callback) => {
  return isFunction(callback) ? callback : () => callback;
};
function size(data, isCompact = false) {
  if (isCompact) {
    data = isString(data) ? data.trim() : compact(data);
  }
  if (isObject(data)) {
    return Object.keys(data).length;
  }
  return data.length;
}
function omit(obj, ...keys) {
  if (isArray(obj)) {
    return obj.reduce((acc, currentValue) => {
      const _item = omit(currentValue, ...keys);
      acc.push(_item);
      return acc;
    }, []);
  } else if (isObject(obj)) {
    const _obj = { ...obj };
    for (const key of keys) {
      delete _obj[key];
    }
    return _obj;
  }
  return obj;
}
function pick(obj, ...keys) {
  if (isArray(obj)) {
    return obj.map((item) => pick(item, ...keys));
  } else if (isObject(obj)) {
    const _obj = {};
    for (const key of keys) {
      _obj[key] = obj[key];
    }
    return _obj;
  }
  return obj;
}

const notUndefined = (val) => val !== void 0;
const isDefined = (val) => val !== void 0 && val !== null;
const isUndefined = (val) => !isDefined(val);

const isObject = (val) => toString.call(val) === "[object Object]";
const isRegex = (val) => toString.call(val) === "[object RegExp]";
const isArray = (val) => Array.isArray(val);
const isNumber = (val) => typeof val === "number";
const isNumeric = (val) => isNumber(val) || /^-?\d+(\.\d+)?$/.test(val);
const isString = (val) => typeof val === "string";
const isBoolean = (val) => typeof val === "boolean";
const isFunction = (val) => typeof val === "function";
const isBrowser = typeof window !== "undefined";
const isArrayLike = (object) => {
  if (!object) {
    return false;
  }
  if (isString(object)) {
    return false;
  }
  if (typeof object !== "object") {
    return false;
  }
  if (object.nodeType === 1) {
    return object.length > 0;
  }
  if (object.length === 0) {
    return true;
  }
  if (object.length > 0) {
    return has(object, 0) && has(object, object.length - 1);
  }
  return false;
};
const isEqual = (value, other) => {
  if (typeof value !== typeof other) {
    return false;
  }
  if (isArray(value) && isArray(other)) {
    return clearUndefined(value).sort().toString() === clearUndefined(other).sort().toString();
  }
  if (isObject(value) && isObject(other)) {
    return JSON.stringify(Object.entries(clearUndefined(value)).sort()) === JSON.stringify(Object.entries(clearUndefined(other)).sort());
  }
  return value === other;
};
const isStrictEqual = (value, other) => {
  if (typeof value !== typeof other) {
    return false;
  }
  if (isArray(value) && isArray(other)) {
    return cloneDeep(value).sort().toString() === cloneDeep(other).sort().toString();
  }
  if (isObject(value) && isObject(other)) {
    return JSON.stringify(Object.entries(value).sort()) === JSON.stringify(Object.entries(other).sort());
  }
  return value === other;
};
const isEmpty = (val) => {
  if (!isDefined(val)) {
    return true;
  }
  if (isString(val) && val.trim() === "") {
    return true;
  }
  if (isNumber(val) && (val === 0 || Number.isNaN(val))) {
    return true;
  }
  if (isBoolean(val) && !val) {
    return true;
  }
  if (isArray(val) && val.length === 0) {
    return true;
  }
  if (isObject(val)) {
    const values = Object.values(clearUndefined(val));
    return values.length === 0;
  }
  return false;
};
const isExist = (val) => !isEmpty(val);

const hasOwnProperty = (obj, v) => {
  if (!isObject(obj)) {
    return false;
  }
  return Object.prototype.hasOwnProperty.call(obj, v);
};
const has = (object, property) => {
  return notUndefined(object[property]);
};
const get = (obj, path, defaultVal) => {
  const _path = isArray(path) ? path : path.replace(/(\[(\d)])/g, ".$2").replace(/^\./, "").split(".").filter((i) => i.length);
  if (obj && _path.length > 0) {
    return get(obj[_path.shift()], _path, defaultVal);
  }
  return obj === void 0 ? defaultVal : obj;
};
const entries = (object) => {
  return Object.entries(object);
};
const map = (object, callback) => {
  return Object.fromEntries(
    entries(object).map(([key, value]) => {
      return [key, callback.call(object, value, key, object)];
    })
  );
};
const filter = (object, callback) => {
  return Object.fromEntries(
    entries(object).filter(([key, value]) => {
      return callback.call(object, value, key, object);
    })
  );
};
const forEach = (data, callback) => {
  for (const [index, [key, value]] of entries(data).entries()) {
    callback.call(data, value, key, index, data);
  }
};
const reduce = (data, callback, initialValue) => {
  return entries(data).reduce((acc, [key, value]) => {
    return callback.call(data, acc, value, key);
  }, initialValue);
};

const toArray = (value) => {
  if (!isDefined(value)) {
    return [];
  }
  if (isArray(value)) {
    return value;
  }
  if (isArrayLike(value)) {
    return Array.prototype.slice.call(value);
  }
  return [value];
};
function range(start, stop, step = 1) {
  const arr = [];
  let current = start;
  const isReversed = start > stop;
  if (isReversed) {
    while (current > stop) {
      arr.push(current);
      current -= step;
    }
  } else {
    while (current < stop) {
      arr.push(current);
      current += step;
    }
  }
  return arr;
}
const chuck = (array, length, zigzag = false) => {
  const chucks = [];
  if (zigzag) {
    for (const [i, element] of array.entries()) {
      const chunkIndex = i % length;
      if (!chucks[chunkIndex]) {
        chucks[chunkIndex] = [];
      }
      chucks[chunkIndex].push(element);
    }
  } else {
    for (let i = 0; i < array.length; i += length) {
      chucks.push(array.slice(i, i + length));
    }
  }
  return chucks;
};
function without(array, ...args) {
  let transform;
  let ignoreItems;
  const [_ignoreItems, options] = args;
  const isRealOptions = isObject(options) && hasOwnProperty(options, "transform");
  if (!options || !isRealOptions) {
    transform = (item) => item;
    ignoreItems = isArray(_ignoreItems) ? _ignoreItems : args;
  } else if (isRealOptions) {
    transform = options.transform;
    ignoreItems = toArray(_ignoreItems);
  }
  return array.filter((item) => !ignoreItems.includes(transform(item)));
}
const groupBy = (array, transform) => {
  return array.reduce(
    (group, item) => {
      const key = transform(item);
      group[key] = group[key] || [];
      group[key].push(item);
      return group;
    },
    {}
  );
};
const join = (array, separator, transform = (item) => item) => {
  return array.map((element) => transform(element)).filter((element) => isExist(element)).join(separator);
};
function at(array, index) {
  const length = array.length;
  if (!length) {
    return void 0;
  }
  if (index < 0) {
    index += length;
  }
  return array[index];
}
function last(array) {
  return at(array, -1);
}
const shuffle = (array) => {
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};
const uniq = (array, transform = (item) => item) => {
  const map = /* @__PURE__ */ new Map();
  for (const item of array) {
    const key = transform(item);
    if (!map.has(key)) {
      map.set(key, item);
    }
  }
  return [...map.values()];
};
const splice = (array, start, end) => {
  const length = array.length;
  if (isUndefined(end)) {
    end = length;
  }
  if (start < 0) {
    start += length;
  }
  if (end < start) {
    return [];
  }
  const _newArray = cloneDeep(array);
  _newArray.splice(start, end - start);
  return _newArray;
};
const limit = (array, count) => {
  return splice(array, count);
};
const sample = (array, n) => {
  return limit(shuffle(array), n);
};
const find = (array, key, value) => {
  return array.find((item) => get(item, key) === value);
};

const getFileExtension = (fileName) => {
  return fileName.slice(fileName.lastIndexOf(".") + 1);
};
const getFileName = (fileName) => {
  return fileName.slice(0, Math.max(0, fileName.lastIndexOf(".")));
};
const downloadImageByUrl = (url, filename) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "";
  document.body.append(a);
  a.click();
  a.remove();
};
const toBlob = async (url) => {
  const response = await fetch(url, { mode: "no-cors" });
  return await response.blob();
};
const toFormData = (data) => {
  const formData = new FormData();
  for (const key in data) {
    const value = data[key];
    if (isArray(value)) {
      for (const item of value) {
        formData.append(`${key}[]`, item);
      }
    } else {
      formData.append(key, value);
    }
  }
  return formData;
};
const bytesToSize = (bytes, unit = 1024) => {
  if (bytes === 0) {
    return "0 Byte";
  }
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const index = Math.floor(Math.log(bytes) / Math.log(unit));
  const roundedValue = Math.round(bytes / Math.pow(unit, index));
  return `${roundedValue} ${sizes[index]}`;
};
const getImageDimensions = (file) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.addEventListener("load", () => {
      resolve({
        height: img.height,
        width: img.width
      });
    });
    img.src = URL.createObjectURL(file);
  });
};

const regexBase64 = "(?:[A-Za-z0-9+\\/]{4})*(?:[A-Za-z0-9+\\/]{2}==|[A-Za-z0-9+/]{3}=)?(?:[A-Za-z0-9+\\/]{4})*(?:[A-Za-z0-9+\\/]{2}(==)?|[A-Za-z0-9+\\/]{3}=?)?";
const regexMimeBase64 = "data:(.*);base64,";
const REGEXP_BASE_64 = new RegExp(`^${regexBase64}$`, "i");
const REGEX_MIME_BASE_64 = new RegExp(`^(${regexMimeBase64})(${regexBase64})$`, "i");
const mimeTypeSignatures = {
  JVBERi0: "application/pdf",
  R0lGODdh: "image/gif",
  R0lGODlh: "image/gif",
  iVBORw0KGgo: "image/png",
  "/9j/": "image/jpg"
};
const detectMimeType = (string) => {
  for (const key in mimeTypeSignatures) {
    if (string.startsWith(key)) {
      return mimeTypeSignatures[key];
    }
  }
};
const toBase64 = async (data) => {
  const blob = isString(data) ? await toBlob(data) : data;
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};
const isBase64 = (string) => REGEXP_BASE_64.test(string);
const isMimeBase64 = (string) => REGEX_MIME_BASE_64.test(string);
const withMimeBase64 = (base64) => {
  if (isMimeBase64(base64)) {
    return base64;
  }
  const mimeType = detectMimeType(base64);
  return mimeType ? `data:${mimeType};base64,${base64}` : base64;
};
const getMimeBase64 = (base64) => {
  const matches = REGEX_MIME_BASE_64.exec(base64);
  return matches ? matches[2] : void 0;
};

const hexToRGB = (hex) => {
  hex = hex.replace(/^#/, "");
  if (!/^(?:[\dA-Fa-f]{3}){1,2}$/.test(hex)) {
    return null;
  }
  const bigint = Number.parseInt(hex, 16);
  const r = bigint >> 16 & 255;
  const g = bigint >> 8 & 255;
  const b = bigint & 255;
  return {
    r,
    g,
    b
  };
};
const rgbToHex = (r, g, b) => {
  if (r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255) {
    return null;
  }
  const hexR = r.toString(16).padStart(2, "0");
  const hexG = g.toString(16).padStart(2, "0");
  const hexB = b.toString(16).padStart(2, "0");
  return `#${hexR}${hexG}${hexB}`;
};

function pipe(fn1, ...fns) {
  const piped = fns.reduce(
    (prevFn, nextFn) => (value) => nextFn(prevFn(value)),
    (value) => value
  );
  return (...args) => piped(fn1(...args));
}

const buildConstants = (constants, keyIndex) => {
  const mapper = /* @__PURE__ */ new Map();
  forEach(constants, (data, key) => {
    if (!mapper.has(key)) {
      mapper.set(key, data);
    }
    const newKey = data[keyIndex];
    if (!mapper.has(newKey)) {
      mapper.set(newKey, {
        ...data,
        [keyIndex]: key
      });
    }
  });
  const _newConstants = Object.fromEntries(mapper);
  Object.assign(_newConstants, {
    getCoreConstants: () => constants,
    getCoreValues: () => Object.values(constants),
    countConstants: () => Object.keys(constants).length
  });
  return _newConstants;
};

const toNumber = (value) => {
  return isNumber(value) ? value : Number(value);
};
const toNumberic = (value) => {
  return isNumeric(value) ? Number(value) : value;
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("vi-VN").format(toNumber(value));
};

const curry = (fn) => {
  return (...args) => {
    return args.length >= fn.length ? fn(...args) : curry(fn.bind(void 0, ...args));
  };
};

function createMapper(map, missingValue) {
  return (key) => {
    return key ? map[key] : missingValue;
  };
}

const add = (a, b) => a + b;
const sum = (array, transform = (props) => props) => {
  return array.map((element) => transform(element)).reduce((accumulator, element) => add(accumulator, element), 0);
};
const average = (array, transform = (props) => props) => {
  return sum(array, transform) / array.length;
};
const min = (array, transform = (props) => props) => {
  const transformedArray = array.map((element) => transform(element));
  return Math.min(...transformedArray);
};
const max = (array, transform = (props) => props) => {
  const transformedArray = array.map((element) => transform(element));
  return Math.max(...transformedArray);
};

const randomString = (length = 5) => {
  let result = "";
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

const upperCaseLetter = "\\x41-\\x5a\\xc0-\\xd6\\xd8-\\xde\\u0100\\u0102\\u0104\\u0106\\u0108\\u010a\\u010c\\u010e\\u0110\\u0112\\u0114\\u0116\\u0118\\u011a\\u011c\\u011e\\u0120\\u0122\\u0124\\u0126\\u0128\\u012a\\u012c\\u012e\\u0130\\u0132\\u0134\\u0136\\u0139\\u013b\\u013d\\u013f\\u0141\\u0143\\u0145\\u0147\\u014a\\u014c\\u014e\\u0150\\u0152\\u0154\\u0156\\u0158\\u015a\\u015c\\u015e\\u0160\\u0162\\u0164\\u0166\\u0168\\u016a\\u016c\\u016e\\u0170\\u0172\\u0174\\u0176\\u0178\\u0179\\u017b\\u017d\\u0181\\u0182\\u0184\\u0186\\u0187\\u0189-\\u018b\\u018e-\\u0191\\u0193\\u0194\\u0196-\\u0198\\u019c\\u019d\\u019f\\u01a0\\u01a2\\u01a4\\u01a6\\u01a7\\u01a9\\u01ac\\u01ae\\u01af\\u01b1-\\u01b3\\u01b5\\u01b7\\u01b8\\u01bc\\u01c4\\u01c5\\u01c7\\u01c8\\u01ca\\u01cb\\u01cd\\u01cf\\u01d1\\u01d3\\u01d5\\u01d7\\u01d9\\u01db\\u01de\\u01e0\\u01e2\\u01e4\\u01e6\\u01e8\\u01ea\\u01ec\\u01ee\\u01f1\\u01f2\\u01f4\\u01f6-\\u01f8\\u01fa\\u01fc\\u01fe\\u0200\\u0202\\u0204\\u0206\\u0208\\u020a\\u020c\\u020e\\u0210\\u0212\\u0214\\u0216\\u0218\\u021a\\u021c\\u021e\\u0220\\u0222\\u0224\\u0226\\u0228\\u022a\\u022c\\u022e\\u0230\\u0232\\u023a\\u023b\\u023d\\u023e\\u0241\\u0243-\\u0246\\u0248\\u024a\\u024c\\u024e";
const lowerCaseLetter = "a-z\\xB5\\xDF-\\xF6\\xF8-\\xFF\\u0101\\u0103\\u0105\\u0107\\u0109\\u010B\\u010D\\u010F\\u0111\\u0113\\u0115\\u0117\\u0119\\u011B\\u011D\\u011F\\u0121\\u0123\\u0125\\u0127\\u0129\\u012B\\u012D\\u012F\\u0131\\u0133\\u0135\\u0137\\u0138\\u013A\\u013C\\u013E\\u0140\\u0142\\u0144\\u0146\\u0148\\u0149\\u014B\\u014D\\u014F\\u0151\\u0153\\u0155\\u0157\\u0159\\u015B\\u015D\\u015F\\u0161\\u0163\\u0165\\u0167\\u0169\\u016B\\u016D\\u016F\\u0171\\u0173\\u0175\\u0177\\u017A\\u017C\\u017E-\\u0180\\u0183\\u0185\\u0188\\u018C\\u018D\\u0192\\u0195\\u0199-\\u019B\\u019E\\u01A1\\u01A3\\u01A5\\u01A8\\u01AA\\u01AB\\u01AD\\u01B0\\u01B4\\u01B6\\u01B9\\u01BA\\u01BD-\\u01BF\\u01C6\\u01C9\\u01CC\\u01CE\\u01D0\\u01D2\\u01D4\\u01D6\\u01D8\\u01DA\\u01DC\\u01DD\\u01DF\\u01E1\\u01E3\\u01E5\\u01E7\\u01E9\\u01EB\\u01ED\\u01EF\\u01F0\\u01F3\\u01F5\\u01F9\\u01FB\\u01FD\\u01FF\\u0201\\u0203\\u0205\\u0207\\u0209\\u020B\\u020D\\u020F\\u0211\\u0213\\u0215\\u0217\\u0219\\u021B\\u021D\\u021F\\u0221\\u0223\\u0225\\u0227\\u0229\\u022B\\u022D\\u022F\\u0231\\u0233-\\u0239\\u023C\\u023F\\u0240\\u0242\\u0247\\u0249\\u024B\\u024D\\u024F";
const diacriticalMark = "\\u0300-\\u036F\\u1AB0-\\u1AFF\\u1DC0-\\u1DFF\\u20D0-\\u20FF\\uFE20-\\uFE2F";
const digit = "\\d";
const dingbatBlock = "\\u2700-\\u27BF";
const whitespace = "\\s\\uFEFF\\xA0";
const nonCharacter = "\\x00-\\x2F\\x3A-\\x40\\x5B-\\x60\\x7b-\\xBF\\xD7\\xF7";
const generalPunctuationBlock = "\\u2000-\\u206F";
const REGEXP_WORD = new RegExp(
  "(?:[" + upperCaseLetter + "][" + diacriticalMark + "]*)?(?:[" + lowerCaseLetter + "][" + diacriticalMark + "]*)+|(?:[" + upperCaseLetter + "][" + diacriticalMark + "]*)+(?![" + lowerCaseLetter + "])|[" + digit + "]+|[" + dingbatBlock + "]|[^" + nonCharacter + generalPunctuationBlock + whitespace + "]+",
  "g"
);
const REGEXP_EXTENDED_ASCII = /^[\u0001-\u00FF]*$/;
const REGEXP_LATIN_WORD = /[A-Z\u00C0-\u00D6\u00D8-\u00DE]?[a-z\u00DF-\u00F6\u00F8-\u00FF]+|[A-Z\u00C0-\u00D6\u00D8-\u00DE]+(?![a-z\u00DF-\u00F6\u00F8-\u00FF])|\d+/g;
const REGEXP_VN_PHONE = /^(0|\+84)(\s|\.)?((3[2-9])|(5[25689])|(7[06-9])|(8[1-9])|(9[0-46-9]))(\d)(\s|\.)?(\d{3})(\s|\.)?(\d{3})$/;
const REGEXP_EMAIL = /[^\t\n\r @]+@[^\t\n\r @]+\.[^\t\n\r @]+/;
const toRegex = (value) => {
  return isRegex(value) ? value : new RegExp(String(value), "i");
};

const toString$1 = (str, defaultValue = "") => {
  if (isUndefined(str)) {
    return defaultValue;
  }
  if (isString(str)) {
    return str;
  }
  return String(str);
};
const toWords = (string, pattern) => {
  const subjectString = toString$1(string);
  const patternRegExp = isDefined(pattern) ? pattern : REGEXP_EXTENDED_ASCII.test(subjectString) ? REGEXP_LATIN_WORD : REGEXP_WORD;
  return subjectString.match(patternRegExp) || [];
};
const toLowerCase = (string) => {
  return toString$1(string).toLowerCase();
};
const toUpperCase = (string) => {
  return toString$1(string).toUpperCase();
};
const toKebabCase = (string) => {
  return toWords(string).map((element) => toLowerCase(element)).join("-");
};
const toSnakeCase = (string) => {
  return toWords(string).map((element) => toLowerCase(element)).join("_");
};
const toCapitalize = (string) => {
  const subjectString = toLowerCase(string);
  return toUpperCase(subjectString[0]) + subjectString.slice(1);
};
const toCamelCase = (string) => {
  return toWords(string).map((string2, index) => {
    return index === 0 ? toLowerCase(string2) : toCapitalize(string2);
  }).join("");
};
const removeVietnameseTones = (str) => {
  str = str.replace(/[àáâãăạảấầẩẫậắằẳẵặ]/g, "a");
  str = str.replace(/[èéêẹẻẽếềểễệ]/g, "e");
  str = str.replace(/[ìíĩỉị]/g, "i");
  str = str.replace(/[òóôõơọỏốồổỗộớờởỡợ]/g, "o");
  str = str.replace(/[ùúũưụủứừửữự]/g, "u");
  str = str.replace(/[ýỳỵỷỹ]/g, "y");
  str = str.replace(/đ/g, "d");
  str = str.replace(/[ÀÁÂÃĂẠẢẤẦẨẪẬẮẰẲẴẶ]/g, "A");
  str = str.replace(/[ÈÉÊẸẺẼẾỀỂỄỆ]/g, "E");
  str = str.replace(/[ÌÍĨỈỊ]/g, "I");
  str = str.replace(/[ÒÓÔÕƠỌỎỐỒỔỖỘỚỜỞỠỢ]/g, "O");
  str = str.replace(/[ÙÚŨƯỤỦỨỪỬỮỰ]/g, "U");
  str = str.replace(/[ÝỲỴỶỸ]/g, "Y");
  str = str.replace(/Đ/g, "D");
  str = str.replace(/[\u0300\u0301\u0303\u0309\u0323]/g, "");
  str = str.replace(/[\u02C6\u0306\u031B]/g, "");
  str = str.replace(/ + /g, " ");
  str = str.trim();
  str = str.replace(/[!"#$%&'()*+,./:;<=>?@[\\]^_`{|}~-]/g, " ");
  return str;
};

const toCssUnit = (value, unit = "px") => {
  if (!isDefined(value)) {
    return void 0;
  }
  return isNumeric(value) ? `${value}${unit}` : String(value);
};

const DEFAULT_CONFIG = { delimiters: ["{{", "}}"] };
function template(string, data, config) {
  const compiled = (data2, config2) => {
    const _config = Object.assign({}, DEFAULT_CONFIG, config2);
    const [startDelimiter, endDelimiter] = _config.delimiters;
    const placeholderRegex = new RegExp(`${startDelimiter}(.*?)${endDelimiter}`, "g");
    return string.replace(placeholderRegex, (match, key) => {
      const value = get(data2, key.trim());
      return notUndefined(value) ? value : match;
    });
  };
  return data ? compiled(data, config) : compiled;
}

const toTree = (array, { id, parentId }) => {
  const tree = [];
  const lookup = {};
  for (const item of array) {
    const _id = item[id];
    lookup[_id] = {
      data: item,
      children: [],
      level: 0,
      isLeaf: false,
      isExpanded: false,
      isSelected: false
    };
  }
  for (const item of array) {
    const _parentId = item[parentId];
    const _id = item[id];
    const _node = lookup[_id];
    if (_parentId === null) {
      lookup[_id].level = 1;
      tree.push(_node);
    } else {
      const parentNode = lookup[_parentId];
      if (parentNode) {
        lookup[_id].level = parentNode.level + 1;
        parentNode.children.push(_node);
      }
    }
  }
  for (const item of array) {
    const _id = item[id];
    lookup[_id].isLeaf = lookup[_id].children.length === 0;
  }
  return tree;
};
const getChildren = (tree, nodeId, id) => {
  let children = [];
  const findNode = (node) => {
    if (node.data[nodeId] === id) {
      children = node.children;
    } else {
      for (const element of node.children) {
        findNode(element);
      }
    }
  };
  for (const element of tree) {
    findNode(element);
  }
  return children;
};
const forEachTree = (tree, callback) => {
  for (const node of tree) {
    callback(node);
    for (const element of node.children) {
      callback(element);
    }
  }
};
const expandAllNodes = (tree) => {
  forEachTree(tree, (node) => {
    node.isExpanded = true;
  });
};
const collapseAllNodes = (tree) => {
  forEachTree(tree, (node) => {
    node.isExpanded = false;
  });
};
const expandNode = (tree, nodeId, id) => {
  const traverse = (node) => {
    if (node.data[nodeId] === id) {
      node.isExpanded = true;
      return true;
    }
    if (node.children) {
      for (const child of node.children) {
        if (traverse(child)) {
          node.isExpanded = true;
          return true;
        }
      }
    }
    return false;
  };
  for (const node of tree) {
    traverse(node);
  }
  return tree;
};

const isAbsolutePath = (path) => {
  return /^([a-z]+:)/gi.test(path);
};
const isRelativePath = (path) => {
  return !isAbsolutePath(path);
};
const removeQueryParam = (url, parameter) => {
  return url.replace(new RegExp(`[?&]${parameter}=[^&#]*(#.*)?$`), "$1").replace(new RegExp(`([?&])${parameter}=[^&]*&`), "$1");
};
const removeQueryParams = (url, ...parameters) => {
  for (const parameter of parameters) {
    url = removeQueryParam(url, parameter);
  }
  return url;
};

const getYoutubeId = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};
const getYoutubeEmbed = (url) => {
  const baseUrl = "https://www.youtube.com/embed";
  const youtubeId = getYoutubeId(url);
  return youtubeId ? [baseUrl, youtubeId].join("/") : null;
};
const getYoutubeThumbnail = (url, name = "default") => {
  const baseUrl = "https://img.youtube.com/vi";
  const youtubeId = getYoutubeId(url);
  const imageName = name + ".jpg";
  return youtubeId ? [baseUrl, youtubeId, imageName].join("/") : null;
};

export { REGEXP_BASE_64, REGEXP_EMAIL, REGEXP_EXTENDED_ASCII, REGEXP_LATIN_WORD, REGEXP_VN_PHONE, REGEXP_WORD, REGEX_MIME_BASE_64, add, at, average, buildConstants, bytesToSize, chuck, clearUndefined, cloneDeep, collapseAllNodes, compact, createMapper, curry, detectMimeType, downloadImageByUrl, expandAllNodes, expandNode, filter, find, forEach, forEachTree, formatCurrency, get, getChildren, getFileExtension, getFileName, getImageDimensions, getMimeBase64, getYoutubeEmbed, getYoutubeId, getYoutubeThumbnail, groupBy, has, hasOwnProperty, hexToRGB, isAbsolutePath, isArray, isArrayLike, isBase64, isBoolean, isBrowser, isDefined, isEmpty, isEqual, isExist, isFunction, isMimeBase64, isNumber, isNumeric, isObject, isRegex, isRelativePath, isStrictEqual, isString, isUndefined, join, last, limit, map, max, min, notUndefined, omit, pick, pipe, randomString, range, reduce, removeQueryParam, removeQueryParams, removeVietnameseTones, rgbToHex, sample, shuffle, size, sleep, splice, sum, template, toArray, toBase64, toBlob, toCamelCase, toCapitalize, toCssUnit, toFormData, toFunction, toKebabCase, toLowerCase, toNumber, toNumberic, toRegex, toSnakeCase, toString$1 as toString, toTree, toUpperCase, toWords, uniq, withMimeBase64, without };
